// src/components/CommentTemplatesGrid.jsx
//
// Editor for the beacon.otti_comment_template rules that auto-fill boilerplate
// COMMENTS on blank lots during Run OTTI. The desk can edit a rule's text, add
// rules, delete rules, and drag rows to reorder them — priority is derived from
// row order (top = highest), so users never type priority numbers. Save snapshot-
// replaces the rule set on the server (writes gated by otti.impairments:write).
import React, { useState, useEffect, useCallback, useRef, useMemo } from "react";
import {
  Alert,
  Box,
  Button,
  Stack,
  Typography,
  Tooltip,
  IconButton,
  CircularProgress,
  Popover,
} from "@mui/material";
import AddIcon from "@mui/icons-material/Add";
import DeleteOutlineIcon from "@mui/icons-material/DeleteOutline";
import SaveIcon from "@mui/icons-material/Save";
import RefreshIcon from "@mui/icons-material/Refresh";
import InfoOutlinedIcon from "@mui/icons-material/InfoOutlined";
import { useTheme } from "@mui/material/styles";
import { AgGridReact } from "ag-grid-react";
import {
  ModuleRegistry,
  ClientSideRowModelModule,
  RowDragModule,
  TextEditorModule,
  SelectEditorModule,
  ValidationModule,
  themeQuartz,
  colorSchemeDark,
  } from "ag-grid-community";
import {
  useNotification,
  notifyPermissionError,
  getWriteErrorMessage,
  VERSION_CONFLICT_MESSAGE,
} from '@1823-partners/core';
import { getCommentTemplates, saveCommentTemplates, getOttiFieldValues } from "../api";
import {
  blankTemplate,
  validateTemplates,
  buildTemplatePayload,
  normalizeTemplateRows,
  templatesKey,
  loadedSnapshot,
  OTTI_FLG_OPTIONS,
} from "../utils/commentTemplates";

// RowDragModule is not in PamGrid's registration set, so register what this
// standalone grid needs. registerModules is additive/idempotent.
ModuleRegistry.registerModules([
  ClientSideRowModelModule,
  RowDragModule,
  TextEditorModule,
  SelectEditorModule,
  ValidationModule,
]);

// Multi-line explanation of how the matching works, shown in the header info popover.
const MATCHING_HELP = [
  "During Run OTTI, blank comments are auto-filled from these rules (existing or manual comments are never overwritten).",
  "Each rule matches a lot on Sec Owner, Security Type, and OTTI Flag. A blank cell is a wildcard (matches anything); Security Type also accepts a trailing % for a prefix match (e.g. \"Municipal%\").",
  "Rules are checked top-to-bottom — the first rule that matches a lot wins, so put the most specific rules higher. Drag a row to change its priority.",
];

// Typeable combobox cell editor (native <input> + <datalist>): pick a suggested
// value or type a custom one — needed for Security Type, where the user may enter
// a `%` prefix pattern (e.g. "Municipal%") that isn't one of the known values.
// Suggestions come from cellEditorParams.options.
//
// Uses the ag-grid-react v33 *reactive* editor contract (reactiveCustomComponents
// defaults to true): AG Grid owns the value via the `value`/`onValueChange` props,
// so every keystroke must be pushed through onValueChange or it is discarded.
// Exported for unit testing of that value flow.
export const ComboCellEditor = (props) => {
  const { value, onValueChange, options } = props;
  const inputRef = useRef(null);
  const listId = "comment-template-combo-options"; // one editor open at a time
  useEffect(() => {
    inputRef.current?.focus();
    inputRef.current?.select();
  }, []);
  return (
    <>
      <input
        ref={inputRef}
        className="ag-input-field-input ag-text-field-input"
        list={listId}
        placeholder="(any)"
        value={value ?? ""}
        onChange={(e) => onValueChange(e.target.value)}
        style={{ width: "100%", height: "100%", boxSizing: "border-box" }}
      />
      <datalist id={listId}>
        {(options || []).map((opt) => (
          <option key={opt} value={opt} />
        ))}
      </datalist>
    </>
  );
};

// Per-row delete button. Wired to the grid via context so it can stay a plain
// presentational renderer.
const DeleteCellRenderer = (props) => {
  const onClick = () => props.context?.onDeleteRow?.(props.data?.__id);
  return (
    <Tooltip title="Delete template">
      <IconButton size="small" onClick={onClick} aria-label="delete template">
        <DeleteOutlineIcon fontSize="small" />
      </IconButton>
    </Tooltip>
  );
};

const CommentTemplatesGrid = () => {
  const { showNotification } = useNotification();
  const theme = useTheme();
  const [rows, setRows] = useState([]);
  const [loading, setLoading] = useState(false);
  const [saving, setSaving] = useState(false);
  // Baseline of the last-loaded/saved state; dirty is derived by comparing the
  // current rows against it, so reordering and then restoring the original order
  // (or editing a value back) leaves Save disabled.
  const [baselineKey, setBaselineKey] = useState(templatesKey([]));
  const [infoAnchor, setInfoAnchor] = useState(null);
  const [secOwnerOptions, setSecOwnerOptions] = useState([]);
  const [stypeOptions, setStypeOptions] = useState([]);
  // After a 409 the latest rules are shown and the user's unsaved list is kept
  // here, so "Restore my edits" can put it back for them to re-apply.
  const [conflictDraft, setConflictDraft] = useState(null);
  const gridApiRef = useRef(null);
  const idCounter = useRef(0);
  // The {PRIORITY, MODIFIED_DATE} snapshot the rules were loaded from; the
  // save sends it back unchanged so the writer can detect a concurrent save.
  const snapshotRef = useRef([]);
  const nextId = () => `t${idCounter.current++}`;

  const dirty = useMemo(() => templatesKey(rows) !== baselineKey, [rows, baselineKey]);

  const load = useCallback(async () => {
    setLoading(true);
    try {
      const res = await getCommentTemplates();
      snapshotRef.current = loadedSnapshot(res.data || []);
      const data = normalizeTemplateRows(res.data || []).map((r) => ({ __id: nextId(), ...r }));
      setRows(data);
      setBaselineKey(templatesKey(data));
      return true;
    } catch (err) {
      showNotification("Failed to load comment templates", "error");
      return false;
    } finally {
      setLoading(false);
    }
  }, [showNotification]);

  useEffect(() => {
    load();
  }, [load]);

  // Populate the Sec Owner / Security Type dropdowns from the OTTI universe.
  // Non-fatal: on failure the editors simply offer no suggestions.
  useEffect(() => {
    const sortedArray = (x) =>
      (Array.isArray(x) ? [...x] : []).sort((a, b) => String(a).localeCompare(String(b)));
    getOttiFieldValues()
      .then((res) => {
        // Dropdowns are presented alphabetically (the endpoint returns them
        // most-common-first; display order is a frontend concern).
        setSecOwnerOptions(sortedArray(res.data?.sec_owners));
        setStypeOptions(sortedArray(res.data?.stypel3));
      })
      .catch((err) => {
        // Non-fatal: editors just offer no suggestions. Log so an empty dropdown
        // in prod is diagnosable rather than silent.
        console.warn("Failed to load OTTI field values for template dropdowns", err);
      });
  }, []);

  const handleAdd = useCallback(() => {
    setRows((prev) => [...prev, { __id: nextId(), ...blankTemplate() }]);
  }, []);

  const handleDeleteRow = useCallback((id) => {
    setRows((prev) => prev.filter((r) => r.__id !== id));
    // priority is derived from position, so refresh it after a removal
    gridApiRef.current?.refreshCells({ force: true });
  }, []);

  const onCellValueChanged = useCallback((e) => {
    setRows((prev) =>
      prev.map((r) => (r.__id === e.data.__id ? { ...r, [e.colDef.field]: e.newValue } : r))
    );
  }, []);

  // Managed row drag reorders the grid; mirror that order into React state so
  // the derived priority (and the save payload) follow it.
  const onRowDragEnd = useCallback((e) => {
    const ordered = [];
    e.api.forEachNode((node) => ordered.push(node.data));
    setRows(ordered);
    e.api.refreshCells({ force: true });
  }, []);

  const handleSave = useCallback(async () => {
    const err = validateTemplates(rows);
    if (err) {
      showNotification(err, "warning");
      return;
    }
    setSaving(true);
    try {
      const res = await saveCommentTemplates(buildTemplatePayload(rows), snapshotRef.current);
      showNotification(res.data?.message || "Comment templates saved", "success");
      setConflictDraft(null);
      await load();
    } catch (e) {
      // Any 409, not only the writer's version_conflict: the list on screen is
      // stale either way.
      if (e?.response?.status === 409) {
        // Someone else saved since this list was loaded: show their rules and
        // keep this user's list one click away. Never re-send automatically.
        const draft = rows;
        showNotification(getWriteErrorMessage(e, VERSION_CONFLICT_MESSAGE), "warning");
        if (await load()) setConflictDraft(draft);
      } else if (!notifyPermissionError(showNotification, e)) {
        showNotification(getWriteErrorMessage(e, "Failed to save comment templates"), "error");
      }
    } finally {
      setSaving(false);
    }
  }, [rows, showNotification, load]);

  const handleRestoreDraft = useCallback(() => {
    // The reloaded snapshot stays: a save now overwrites the latest rules on
    // purpose, after the user has seen them.
    setRows(conflictDraft || []);
    setConflictDraft(null);
    gridApiRef.current?.refreshCells({ force: true });
  }, [conflictDraft]);

  const onGridReady = useCallback((params) => {
    gridApiRef.current = params.api;
  }, []);

  const columnDefs = useMemo(
    () => [
      {
        headerName: "Priority",
        colId: "priority",
        width: 120,
        editable: false,
        sortable: false,
        rowDrag: true,
        headerTooltip: "Drag a row to reorder. Priority follows order (top = highest).",
        cellStyle: { fontWeight: 600 },
        valueGetter: (p) => (p.node?.rowIndex != null ? p.node.rowIndex + 1 : ""),
      },
      {
        field: "SEC_OWNER",
        headerName: "Sec Owner",
        editable: true,
        width: 140,
        cellEditor: "agSelectCellEditor",
        // Always include the row's current value so agSelectCellEditor never
        // silently coerces an out-of-universe (or fetch-failed) owner to the ''
        // wildcard on edit. '' (= "(any)") is always first.
        cellEditorParams: (p) => {
          const base = ["", ...secOwnerOptions];
          return {
            values: p.value && !base.includes(p.value) ? [...base, p.value] : base,
          };
        },
        valueFormatter: (p) => (p.value == null || p.value === "" ? "(any)" : p.value),
        headerTooltip: "Match the security owner (e.g. RA). (any) = matches any owner.",
      },
      {
        field: "STYPEL3",
        headerName: "Security Type (STYPEL3)",
        editable: true,
        flex: 1,
        minWidth: 220,
        cellEditor: ComboCellEditor,
        cellEditorParams: { options: stypeOptions },
        valueFormatter: (p) => (p.value == null || p.value === "" ? "(any)" : p.value),
        headerTooltip:
          "Match a security type. Pick a suggestion or type a custom value; a trailing % is a prefix match (e.g. 'Municipal%'). (any) = matches any type.",
      },
      {
        field: "OTTI_FLG",
        headerName: "OTTI Flag",
        editable: true,
        width: 180,
        cellEditor: "agSelectCellEditor",
        cellEditorParams: { values: OTTI_FLG_OPTIONS },
        // Stored value stays '' for wildcard; show it as "(any)" in the cell.
        valueFormatter: (p) => (p.value == null || p.value === "" ? "(any)" : p.value),
        headerTooltip: "Match the lot's OTTI flag. (any) = matches any status.",
      },
      { field: "COMMENT_TEXT", headerName: "Comment", editable: true, flex: 2, minWidth: 300 },
      {
        headerName: "",
        colId: "delete",
        width: 70,
        editable: false,
        sortable: false,
        filter: false,
        resizable: false,
        cellRenderer: DeleteCellRenderer,
      },
    ],
    [secOwnerOptions, stypeOptions]
  );

  const defaultColDef = useMemo(() => ({ resizable: true, sortable: false, filter: false }), []);

  const gridTheme = useMemo(
    () => (theme.palette.mode === "dark" ? themeQuartz.withPart(colorSchemeDark) : themeQuartz),
    [theme.palette.mode]
  );

  return (
    <Box sx={{ display: "flex", flexDirection: "column", gap: 1.5 }}>
      <Box>
        <Box sx={{ display: "flex", alignItems: "center", gap: 0.5 }}>
          <Typography variant="h6">Comment Templates</Typography>
          <Tooltip title="How matching works">
            <IconButton
              size="small"
              onClick={(e) => setInfoAnchor(e.currentTarget)}
              aria-label="How comment template matching works"
            >
              <InfoOutlinedIcon fontSize="small" />
            </IconButton>
          </Tooltip>
        </Box>
        <Typography variant="body2" color="text.secondary">
          Boilerplate comments auto-filled on lots with no comment during Run OTTI. Drag a row to set
          its priority (top = highest).
        </Typography>
        <Popover
          open={Boolean(infoAnchor)}
          anchorEl={infoAnchor}
          onClose={() => setInfoAnchor(null)}
          anchorOrigin={{ vertical: "bottom", horizontal: "left" }}
        >
          <Box sx={{ p: 2, maxWidth: 480 }}>
            <Typography variant="subtitle2" gutterBottom>
              How comment-template matching works
            </Typography>
            <Box component="ul" sx={{ m: 0, pl: 2.5 }}>
              {MATCHING_HELP.map((line, i) => (
                <Typography key={i} component="li" variant="body2" sx={{ mb: 0.75 }}>
                  {line}
                </Typography>
              ))}
            </Box>
          </Box>
        </Popover>
      </Box>

      <Stack direction="row" spacing={1} alignItems="center">
        <Button startIcon={<AddIcon />} onClick={handleAdd} variant="outlined" size="small" disabled={saving}>
          Add Template
        </Button>
        <Button
          startIcon={<SaveIcon />}
          onClick={handleSave}
          variant="contained"
          size="small"
          disabled={!dirty || saving}
        >
          {saving ? "Saving…" : "Save"}
        </Button>
        <Button startIcon={<RefreshIcon />} onClick={load} variant="text" size="small" disabled={saving || loading}>
          Refresh
        </Button>
        {(loading || saving) && <CircularProgress size={20} />}
      </Stack>

      {conflictDraft && (
        <Alert
          severity="warning"
          action={
            <Stack direction="row" spacing={1}>
              <Button color="inherit" size="small" onClick={handleRestoreDraft}>
                Restore my edits
              </Button>
              <Button color="inherit" size="small" onClick={() => setConflictDraft(null)}>
                Discard
              </Button>
            </Stack>
          }
        >
          Someone else saved the templates first. Showing their latest rules; your unsaved list is kept.
        </Alert>
      )}

      <Box sx={{ height: "60vh", width: "100%" }}>
        <AgGridReact
          theme={gridTheme}
          rowData={rows}
          columnDefs={columnDefs}
          defaultColDef={defaultColDef}
          getRowId={(p) => String(p.data.__id)}
          rowDragManaged
          animateRows
          singleClickEdit
          stopEditingWhenCellsLoseFocus
          context={{ onDeleteRow: handleDeleteRow }}
          onCellValueChanged={onCellValueChanged}
          onRowDragEnd={onRowDragEnd}
          onGridReady={onGridReady}
        />
      </Box>
    </Box>
  );
};

export default CommentTemplatesGrid;
